import { useTranslation } from 'react-i18next'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import styled from 'styled-components'

import {
  useLazyGetGroupsForAdminQuery,
  useToggleTeamArchiveMutation,
} from 'app/services/group.service'
import { BUTTONS, DROPDOWN, FORM, MODALS, NAVIGATION, PLACEHOLDERS, TABLE } from 'locales/locales'
import { FixMeLater } from 'types'

import CustomButton from 'components/button/CustomButton'
import useInfoModal from 'utils/hooks/useInfoModal'
import Loading from 'components/spinner/Loading'
import { IGroup } from 'types/group.types'
import KGrid from 'components/kendo/grid/KGrid'
import { GridColumn } from '@progress/kendo-react-grid'
import { useAppSelector } from 'app/hooks'
import { IRolePermissions, TeamAndChannelPermissions } from 'types/role.types'
import { isFlagSetIn } from 'utils/helpers/isSetInFlags'
import { icons } from 'utils/icons'
import Dropdown from 'components/kendo/dropdown/DropdownList'
import CreateGroupModal from 'features/settings/management/group-management/create-group/CreateGroupModal'
import GroupInfoModal from 'features/settings/management/group-management/group-info/GroupInfoModal'
import CustomIcon from 'components/icon/CustomIcon'

interface DataStateFilter {
  field: string
  operator: string
  value: boolean | string
}

interface DataState {
  take: number
  skip: number
  filter: {
    logic: string
    filters: DataStateFilter[]
  }
  sort?: Array<{ field: string; dir: string }>
}
const initialDataState: DataState = {
  take: 10,
  skip: 0,
  filter: {
    logic: 'and',
    filters: [{ field: 'isArchived', operator: 'eq', value: false }],
  },
  sort: [],
}

const TeamManagement = () => {
  const { t } = useTranslation()

  const dropDownOptions = useMemo(
    () => [
      {
        title: t(DROPDOWN.ACTIVE_TEAM),
        value: JSON.stringify({ field: 'isArchived', operator: 'eq', value: false }),
      },
      {
        title: t(DROPDOWN.DEACTIVE_TEAM),
        value: JSON.stringify({ field: 'isArchived', operator: 'eq', value: true }),
      },
    ],
    [t],
  )

  const [groupId, setGroupId] = useState('')
  const [visibleCreateGroupModal, setVisibleCreateGroupModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [activeTeamsFilter, setActiveTeamsFilter] = useState(dropDownOptions[0])
  const [currentData, setCurrentData] = useState({ data: [] as FixMeLater[], totalCount: 1 })
  const [getAllGroups] = useLazyGetGroupsForAdminQuery()
  const [channelInfoModalVisible, setChannelInfoModalVisible] = useState(false)
  const [toggleTeamArchivingStatus] = useToggleTeamArchiveMutation()
  const { openModal, closeModal, Modal } = useInfoModal()

  const permissions = useAppSelector<IRolePermissions>((state) => state.auth.permissions)

  const hasCreateTeamPermission = isFlagSetIn(
    [TeamAndChannelPermissions.CREATE_TEAM],
    permissions.teamAndChannel,
  )
  const hasArchiveTeamPermission = isFlagSetIn(
    [TeamAndChannelPermissions.ARCHIVE_TEAM],
    permissions.teamAndChannel,
  )
  const hasUpdateTeamPermission = isFlagSetIn(
    [TeamAndChannelPermissions.UPDATE_TEAM],
    permissions.teamAndChannel,
  )
  const [dataState, setDataState] = useState<DataState>(initialDataState)

  const searchRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    fetchGroups()
  }, [dataState])

  const onConfirmArchiveGroup = async (groupId: string) => {
    try {
      closeModal()
      Loading.ref.show()
      await toggleTeamArchivingStatus({ groupId })
      await fetchGroups()
    } finally {
      Loading.ref.hide()
    }
  }

  const onCloseConfirmArchiveGroup = () => {
    closeModal()
  }

  const onToggleArchive = async (group: IGroup) => {
    const groupId = group._id
    setGroupId(groupId)
    setCurrentData((state) => {
      const clonedState = { ...state }
      const groupIndex = clonedState.data.findIndex((g) => g._id === groupId)
      if (groupIndex !== -1) {
        clonedState.data[groupIndex] = {
          ...clonedState.data[groupIndex],
          isArchived: !clonedState.data[groupIndex].isArchived,
        }
      }
      return clonedState
    })
    openModal({
      title: group.isArchived ? t(MODALS.UNARCHIVE_TITLE) : t(MODALS.ARCHIVE_TITLE),
      description: group.isArchived
        ? t(MODALS.UNARCHIVE_DESCRIPTION)
        : t(MODALS.ARCHIVE_DESCRIPTION),
      cancelTitle: t(BUTTONS.CANCEL) as string,
      okTitle: t(BUTTONS.SAVE) as string,
      onCancel: onCloseConfirmArchiveGroup,
      onOk: () => onConfirmArchiveGroup(groupId),
    })
  }

  const fetchGroups = async () => {
    try {
      setIsLoading(true)
      const data = await getAllGroups({ ...dataState, count: true })
      if (data.data) {
        const modifiedGroups = data.data?.groups.map((group: IGroup) => ({
          ...group,
          actions: {
            actionKey: group?.isArchived
              ? { text: t(BUTTONS.UNARCHIVE), icon: icons.unarchiveNonFilledIcon }
              : { text: t(BUTTONS.ARCHIVE), icon: icons.archiveIcon },
          },
        }))
        setCurrentData({ data: modifiedGroups, totalCount: data.data?.totalCount || 0 })
      }
    } catch (err) {
      console.log(err)
    } finally {
      setIsLoading(false)
    }
  }

  const onToggleCreateGroup = () => {
    setVisibleCreateGroupModal((prevVisible) => !prevVisible)
  }

  const onClickGroup = async (params: FixMeLater) => {
    setGroupId(params._id)
    setChannelInfoModalVisible(true)
  }

  const closeInfoModalVisible = () => {
    setChannelInfoModalVisible(false)
  }

  const handleReloadClick = useCallback(() => {
    setIsLoading(true)
    fetchGroups()
  }, [])

  const ActionCell = useCallback(
    ({ dataItem }: FixMeLater) => {
      return (
        <td>
          <ButtonContainer>
            {hasUpdateTeamPermission && (
              <StyledCustomIcon>
                <CustomIcon
                  source={icons.editIcon}
                  onClick={() => {
                    onClickGroup(dataItem)
                  }}
                  iconPreset="grande"
                  alt="edit"
                />
              </StyledCustomIcon>
            )}
            {hasArchiveTeamPermission && (
              <StyledCustomIcon>
                <CustomIcon
                  iconPreset="grande"
                  source={dataItem.actions.actionKey.icon}
                  onClick={() => {
                    onToggleArchive(dataItem)
                  }}
                  alt="delete"
                />
              </StyledCustomIcon>
            )}
          </ButtonContainer>
        </td>
      )
    },
    [hasUpdateTeamPermission, hasArchiveTeamPermission],
  )

  const handleFilterChange = useCallback(
    (event: FixMeLater) => {
      setDataState({
        ...dataState,
        filter: event.filter,
      })
    },
    [dataState],
  )

  const onDataStateChange = useCallback((event: FixMeLater) => {
    setDataState((prev) => ({
      ...prev,
      skip: event.dataState.skip,
      take: event.dataState.take,
      sort: event.dataState.sort,
    }))
  }, [])

  const getIsArchivedFilter = useCallback(() => {
    return JSON.parse(activeTeamsFilter.value) as DataStateFilter
  }, [activeTeamsFilter])

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.trim()
      const isArchivedFilter = getIsArchivedFilter()

      if (!searchValue) {
        setDataState((prev) => ({
          ...prev,
          filter: { logic: 'and', filters: [isArchivedFilter] as never },
          skip: 0,
        }))
        return
      }

      setDataState((prev) => ({
        ...prev,
        filter: {
          logic: 'and',
          filters: [
            isArchivedFilter,
            {
              logic: 'or',
              filters: [
                { field: 'name', operator: 'contains', value: searchValue },
                { field: 'description', operator: 'contains', value: searchValue },
              ],
            },
          ] as never,
        },
        skip: 0,
      }))
    },
    [getIsArchivedFilter],
  )

  const handleDropdownChange = useCallback(
    (e: FixMeLater) => {
      const parsed = JSON.parse(e.value) as DataStateFilter

      setActiveTeamsFilter({
        title: parsed.value ? dropDownOptions[1].title : dropDownOptions[0].title,
        value: e.value,
      })

      // search'i temizle
      if (searchRef.current) searchRef.current.value = ''

      setDataState((prev) => ({
        ...prev,
        filter: { logic: 'and', filters: [parsed] as never },
        take: 10,
        skip: 0,
      }))
    },
    [dropDownOptions],
  )

  return (
    <Container>
      <Wrapper>
        <ContentContainer>
          <HeaderBox>
            <HeaderTitle>{t(NAVIGATION.TEAMS)}</HeaderTitle>
            <HeaderActions>
              {hasCreateTeamPermission && (
                <AddButton
                  text={`+ ${t(BUTTONS.CREATE_TEAM_BUTTON)}`}
                  textColor="white"
                  onClick={onToggleCreateGroup}
                />
              )}
              <ReloadButton onClick={handleReloadClick}>
                {isLoading ? (
                  <LoadingSpinner />
                ) : (
                  <img
                    className="refresh"
                    height={15}
                    src={icons.refreshIcon.src}
                    srcSet={icons.refreshIcon.srcset}
                  />
                )}
              </ReloadButton>
            </HeaderActions>
          </HeaderBox>

          <FilterContainer>
            <FilterGroup>
              <FilterLabel>{t(FORM.FILTER)}:</FilterLabel>
              <Dropdown
                style={{ width: '250px' }}
                options={dropDownOptions}
                id="team-filter-dropdown"
                selectedOption={activeTeamsFilter}
                onChangeOption={handleDropdownChange}
              />
            </FilterGroup>
            <SearchInput
              ref={searchRef}
              placeholder={t(PLACEHOLDERS.SEARCH_PLACEHOLDER)}
              onChange={handleSearchChange}
            />
          </FilterContainer>

          <TableWrapper>
            <KGrid
              loading={isLoading}
              datasource={currentData.data}
              dataState={dataState}
              pageable={true}
              total={currentData.totalCount || 0}
              editMode={false}
              sortable={true}
              onFilterChange={handleFilterChange}
              onDataStateChange={onDataStateChange}
              style={{ height: '100%' }}
            >
              <GridColumn field="name" title={t(TABLE.TEAM_NAME)} />
              <GridColumn field="description" sortable={false} title={t(TABLE.TEAM_DESCRIPTION)} />
              <GridColumn
                filterable={false}
                sortable={true}
                field="channels"
                title={t(TABLE.TEAM_CHANNELS)}
              />
              <GridColumn
                field="actions"
                filterable={false}
                sortable={false}
                title={t(TABLE.USER_ACTIONS)}
                cell={(props) => <ActionCell {...props} />}
              />
            </KGrid>
          </TableWrapper>
        </ContentContainer>

        {visibleCreateGroupModal && (
          <CreateGroupModal refetch={fetchGroups} onClose={onToggleCreateGroup} />
        )}
        {channelInfoModalVisible && (
          <GroupInfoModal
            groupId={groupId}
            fetchGroups={fetchGroups}
            onClose={closeInfoModalVisible}
          />
        )}
      </Wrapper>
      {Modal}
    </Container>
  )
}

export default TeamManagement

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
`

const ContentContainer = styled.div`
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  background-color: white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  display: flex;
  flex-direction: column;
  flex: 1;
`

const HeaderBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #e6e6e6;
`

const HeaderTitle = styled.h1`
  font-size: 20px;
  font-weight: 500;
  margin: 0;
  color: #333;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const AddButton = styled(CustomButton)`
  background-color: #1e4ab7;
  border-radius: 4px;
  font-weight: 500;
  padding: 8px 16px;
  height: 30.5px;
`

const ReloadButton = styled.button`
  width: 30px;
  height: 30.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;

  &:hover {
    background-color: #f5f5f5;
  }
`

const TableWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 20px;
  border-bottom: 1px solid #e6e6e6;
  background-color: #f9f9f9;
  justify-content: space-between;
`

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: white;
  width: 200px;
  font-size: 14px;

  &::placeholder {
    color: #888;
  }

  &:focus {
    outline: none;
    border-color: #129fdb;
    box-shadow: 0 0 0 2px rgba(18, 159, 219, 0.1);
  }
`
const FilterLabel = styled.div`
  font-size: 14px;
  color: #333;
  margin-right: 10px;
`

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
`

const StyledCustomIcon = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;
  cursor: pointer;
  &:hover {
    border-radius: 28px;
    background-color: #e0e0e0;
  }
  padding: 4px 6px;
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 6px;
  margin-right: 10px;
  flex-wrap: wrap;
`

const LoadingSpinner = styled.div`
  width: 15px;
  height: 15px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`
